/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import { TerminalPlatformsEnum } from '../../../../shared/entities/terminalPlatform';
import { DetailsPanelGroupCollapseState } from '../../../../store/ui/reducer';
import { isOnline } from '../../../../utils/ConnectionStatusUtils';
import { bytesToUnitString } from '../../../../utils/DataAmountUtils';
import { isPlatformTypeInList } from '../../../../utils/PlatformTypeUtils';
import { getTerminalTypeString, toFixedValueFormatter } from '../../../../utils/StringUtils';
import { getGxSla, toCommaSeparatedString, uptimeFormatter } from '../../../../utils/TerminalUtilities';
import CollapseGroup from '../../../common/collapse_group/CollapseGroup';
import CollapseGroupItem from '../../../common/collapse_group/CollapseGroupItem';
import ConnectivityCollapseGroupItem from '../../../common/collapse_group/ConnectivityCollapseGroupItem';
import LookAnglesContainerWrapper from '../LookAnglesPanel/LookAnglesContainerWrapper';
import SatellitesInFovContainer from '../SatellitesInFov/SatellitesInFovContainer';
import { calculateScrollbarHeightOffset, CollapseGroupScrollbarContainer } from './TerminalTabPanel';
import CollapseGroupItemTerminalStatus from './shared/CollapseGroupItemTerminalStatus';
import { FulltailViewButton } from './shared/FulltailViewButton';

export interface TerminalTabDataGxStateProps {
  hidden: boolean;
  isOverviewMode: boolean;
  isFullTailViewMode: boolean;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
  history: any;
}

export interface TerminalTabDataGxDispatchProps {
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
}

interface TerminalTabDataGxProps extends TerminalTabDataGxStateProps, TerminalTabDataGxDispatchProps {}

export interface ScrollbarProps {
  scrollbarHeightOffset: number;
}

const StartSpacer = styled.div`
  height: 20px;
`;

const GroupDivider = styled.div`
  height: 15px;
`;

const TerminalTabDataGX = ({
  hidden,
  isOverviewMode,
  isFullTailViewMode,
  tailDetailsPanelTerminal,
  detailsPanelGroupCollapseState,
  setDetailsPanelGroupCollapseState,
  history,
}: TerminalTabDataGxProps) => {
  // Local State
  const [highlightedSatelliteInFov, setHighlightedSatelliteInFov] = useState(undefined);

  function toggleGroupCollapsed(groupName: keyof DetailsPanelGroupCollapseState) {
    setDetailsPanelGroupCollapseState({
      ...detailsPanelGroupCollapseState,
      [groupName]: !detailsPanelGroupCollapseState[groupName],
    });
  }

  let isOffline = !isOnline((tailDetailsPanelTerminal || {}).connectivityStatus);

  const scrollbarHeightOffset = calculateScrollbarHeightOffset(tailDetailsPanelTerminal);

  const terminalProps = {
    highlightedSatelliteInFov: highlightedSatelliteInFov,
    setHighlightedSatelliteInFov: setHighlightedSatelliteInFov,
    terminalDetails: tailDetailsPanelTerminal,
    isOverviewMode: isOverviewMode,
  };

  return !hidden ? (
    <React.Fragment>
      <LookAnglesContainerWrapper {...terminalProps} />
      <SatellitesInFovContainer {...terminalProps} />
      {!isFullTailViewMode && (
        <FulltailViewButton tailDetailsPanelTerminal={tailDetailsPanelTerminal} history={history} />
      )}
      <CollapseGroupScrollbarContainer scrollbarHeightOffset={scrollbarHeightOffset}>
        <StartSpacer />
        <CollapseGroup
          data-test="terminal__status__collapse__group__header--current__activity"
          collapseButtonClicked={() => {
            toggleGroupCollapsed('currentActivityGroupOpened');
          }}
          open={detailsPanelGroupCollapseState.currentActivityGroupOpened}
          title="Activity"
        >
          <div className={isOffline ? 'tail__details_faded_wrapper' : ''}>
            <ConnectivityCollapseGroupItem terminal={tailDetailsPanelTerminal} />
            <CollapseGroupItem
              id="gx_fl_sla"
              label="FL SLA"
              value={getGxSla(
                tailDetailsPanelTerminal?.gxDownstreamCirInKbps,
                tailDetailsPanelTerminal?.gxDownstreamMirInKbps,
              )}
            />
            <CollapseGroupItem
              id="gx_rl_sla"
              label="RL SLA"
              value={getGxSla(
                tailDetailsPanelTerminal?.gxUpstreamCirInKbps,
                tailDetailsPanelTerminal?.gxUpstreamMirInKbps,
              )}
            />
            <CollapseGroupItem
              id="gx_fl_data_use"
              label="FL Data Use"
              value={tailDetailsPanelTerminal?.gxTotalReceived}
              valueFormatter={bytesToUnitString}
            />
            <CollapseGroupItem
              id="gx_rl_data_use"
              label="RL Data Use"
              value={tailDetailsPanelTerminal?.gxTotalTransmitted}
              valueFormatter={bytesToUnitString}
            />
            <CollapseGroupItem
              id="gx_latency"
              label="Latency"
              value={tailDetailsPanelTerminal?.gxLatency}
              valueFormatter={toFixedValueFormatter(2, ' ms')}
            />
          </div>
        </CollapseGroup>
        <CollapseGroup
          data-test="terminal__status__collapse__group__header--situational__capabilities"
          collapseButtonClicked={() => {
            toggleGroupCollapsed('situationalCapabilitiesGroupOpened');
          }}
          open={detailsPanelGroupCollapseState.aircraftStatusGroupOpened}
          title="Situational Capabilities"
        >
          <div className={isOffline ? 'tail__details_faded_wrapper' : ''}>
            <CollapseGroupItem
              id="network"
              label="Network"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.networkGroup : null}
            />
            <CollapseGroupItem
              id="hub"
              label="Hub"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.hubName : null}
            />
            <CollapseGroupItem
              id="beam"
              label="Beam"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.beamDisplayName : null}
            />
            <CollapseGroupItem
              id="service_region"
              label="Service Region"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.serviceRegionName : null}
            />
          </div>
        </CollapseGroup>
        <CollapseGroupItemTerminalStatus
          isOffline={isOffline}
          tailDetailsPanelTerminal={tailDetailsPanelTerminal}
          detailsPanelGroupCollapseState={detailsPanelGroupCollapseState}
          toggleGroupCollapsed={toggleGroupCollapsed}
        >
          <CollapseGroupItem
            id="altitude"
            label="Altitude"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.altitudeFeet : null}
            valueFormatter={toCommaSeparatedString(' ft')}
            truncate
            hidden={
              tailDetailsPanelTerminal &&
              !isPlatformTypeInList(tailDetailsPanelTerminal.platformType, TerminalPlatformsEnum.Airborne)
            }
          />
        </CollapseGroupItemTerminalStatus>
        <CollapseGroup
          data-test="terminal__status__collapse__group__header--tail__configuration"
          collapseButtonClicked={() => {
            toggleGroupCollapsed('tailConfigurationGroupOpened');
          }}
          open={detailsPanelGroupCollapseState.tailConfigurationGroupOpened}
          title="Terminal Configuration"
        >
          <div className={isOffline ? 'tail__details_faded_wrapper' : ''}>
            <CollapseGroupItem
              id="terminalType"
              label="Terminal Type"
              value={getTerminalTypeString(tailDetailsPanelTerminal)}
            />
            <CollapseGroupItem
              id="terminalSerialNumber"
              label="Terminal Serial Number"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.aircraftSerialNumber : null}
            />
            <CollapseGroupItem
              id="terminal_device_id"
              label="Terminal Device ID (DID)"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.gxDid : null}
            />
            <CollapseGroupItem
              id="terminal_rmp"
              label="CLIN/Service Tier"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.gxTerminalRmp : null}
            />
            <GroupDivider />
            <CollapseGroupItem
              id="uptime"
              label="Uptime"
              value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.gxUptime : null}
              valueFormatter={uptimeFormatter}
            />
          </div>
        </CollapseGroup>
      </CollapseGroupScrollbarContainer>
    </React.Fragment>
  ) : null;
};

export default TerminalTabDataGX;
